// Browser-native PDF export. The previous html2canvas approach choked on
// our oklch() / color-mix() theme, so we delegate to the browser's print
// engine instead — it understands every modern CSS value the app already
// uses. The user gets a native print dialog with "Save as PDF" as one of
// the destinations; the page itself is styled via `@media print` rules
// in styles.css so only the report content prints.

import type { ValidateResponse } from "./api.js";

export interface PdfInput {
  response: ValidateResponse;
  sha256: string;
  durationMs: number;
  shared?: { createdAt: string; expiresAt: string };
  url?: string;
}

/**
 * Prepare the page, then trigger the browser's print-to-PDF dialog.
 *
 * The rule for the printed copy is intentionally simple: the report
 * prints the way the page looks, backgrounds and all. The only per-
 * section tweak is "expand fail + warn, collapse pass" — so readers
 * see every blocking issue in full without wading through the long
 * passed list.
 */
export async function downloadReportPdf(input: PdfInput): Promise<void> {
  const results = document.querySelector<HTMLElement>(".results");
  if (!results) return;

  // Wait for web fonts so the printed output uses the same typography as
  // the live page rather than a serif fallback.
  const fontsReady = (document as unknown as { fonts?: { ready?: Promise<unknown> } }).fonts
    ?.ready;
  if (fontsReady) await fontsReady;

  // Open failed + warning sections; collapse the passed section so the
  // PDF shows every blocking issue in full but only a one-line summary
  // of the passing checks. Snapshot previous state so we can restore
  // after the dialog closes.
  const failWarnSections = Array.from(
    results.querySelectorAll<HTMLElement>(".section.failed, .section.warn"),
  );
  const prevFailWarnOpen = failWarnSections.map((s) => s.classList.contains("open"));
  failWarnSections.forEach((s) => s.classList.add("open"));

  const passSection = results.querySelector<HTMLElement>(".section.passed");
  const prevPassOpen = passSection?.classList.contains("open") ?? false;
  passSection?.classList.remove("open");

  // Expand every "Show N more" list but only inside expanded sections —
  // leave the collapsed pass list alone.
  const hiddenLists = Array.from(
    results.querySelectorAll<HTMLElement>(
      ".section.failed .list-hidden, .section.warn .list-hidden",
    ),
  );
  const prevHidden = hiddenLists.map((n) => n.hidden);
  hiddenLists.forEach((n) => (n.hidden = false));

  // Browser uses `document.title` as the default Save-as-PDF filename.
  const prevTitle = document.title;
  document.title = `${safeBase(input.response.file.name)}-report`;

  document.body.classList.add("is-printing");

  try {
    window.print();
  } finally {
    document.body.classList.remove("is-printing");
    document.title = prevTitle;
    failWarnSections.forEach((s, i) => {
      if (prevFailWarnOpen[i]) s.classList.add("open");
      else s.classList.remove("open");
    });
    if (prevPassOpen) passSection?.classList.add("open");
    else passSection?.classList.remove("open");
    hiddenLists.forEach((n, i) => (n.hidden = prevHidden[i] ?? true));
  }
}

function safeBase(name: string): string {
  return name.replace(/\.zip$/i, "").replace(/[^a-z0-9_-]+/gi, "_") || "report";
}
